'use client';

import { extractQueryParams, extractRouteParams } from '@/domains/routing';
import { FEATURE_FLAGS } from '@/constants';
import { useEffect } from 'react';
import { useGlobalStore } from '@/store';
import { useParams } from 'next/navigation';

/**
 * Extracts dynamic route parameters and query string values, then syncs
 * the global store's "active" selections to match the current URL.
 *
 * Supported URL patterns:
 * - `/sales-rep/certifications/{certificationId}`
 * - `/sales-rep/certifications/{certificationId}/simulations/{simulationId}/...`
 * - Any of the above with `?sessionId=<id>` appended
 * - `/manager/users/{userId}` — sets the manager's active user
 *
 * Side effects (via `useEffect`):
 * - `setActiveCertification` — when a matching certification is found.
 * - `setActiveSimulation`    — when a matching simulation is found inside that certification.
 * - `setActivePersona`       — when the resolved simulation has an associated persona.
 * - `setActiveSessionId`     — when a `sessionId` query parameter is present.
 * - `setManagerActiveUserId` — when a `userId` route param is present and the
 *                              `ENABLE_MANAGER_API_ENDPOINTS` feature flag is on.
 *
 * @returns An object containing the extracted `certificationId`, `simulationId`,
 *          and `sessionId` strings (or `null` when absent) so callers can read
 *          them without importing the router themselves.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useActiveCertificationFromRoute();
 *
 *   const { activeCertification, activeSimulation } = useGlobalStore();
 *   // activeCertification and activeSimulation will be set based on route params
 *
 *   const { activeSessionId } = useGlobalStore();
 *   // activeSessionId will be set if sessionId query param is present
 * }
 * ```
 */
export const useActiveCertificationFromRoute = () => {
  // ================================
  // Route & query param extraction
  // ================================

  /**
   * Pull dynamic segment values from the Next.js route.
   * `extractRouteParams` normalises each value to a string or null so
   * all downstream checks can use strict equality without type-narrowing.
   */
  const { certificationId, simulationId, userId } = extractRouteParams(useParams());

  /**
   * Read the raw query string from `window.location.search` (client-side only).
   * Falls back to an empty string on the server so `extractQueryParams` still
   * returns a clean object rather than throwing.
   */
  const urlParams = extractQueryParams(typeof window !== 'undefined' ? window.location.search : '');

  /** `sessionId` query param value, or `null` when absent. */
  const sessionId = urlParams['sessionId'] || null;

  // ================================
  // Global store bindings
  // ================================
  const {
    certifications,
    personas,
    setActiveCertification,
    setActivePersona,
    setActiveSessionId,
    setActiveSimulation,
    setManagerActiveUserId,
    setManagerActiveUserIsLoading,
    simulations,
  } = useGlobalStore();

  // ================================
  // Sync effect
  // ================================
  /**
   * Runs whenever any route/query param or store collection changes.
   *
   * The logic is intentionally nested so that deeper lookups (simulation,
   * persona) only execute when their parent lookups succeed. This prevents
   * partial state updates where, for example, a simulation is activated
   * without a corresponding certification.
   */
  useEffect(() => {
    // ==========================================
    // Certification → Simulation → Persona → Session
    // ==========================================
    if (certificationId) {
      // Find and set the active certification
      const certificationMatch = certifications.find((c) => c.id.toString() === certificationId);

      if (certificationMatch) {
        setActiveCertification(certificationMatch);

        // If a simulationId is provided, find and set the active simulation
        if (simulationId) {
          const simulationMatch = simulations.find((s) => s.id.toString() === simulationId);

          if (simulationMatch) {
            setActiveSimulation(simulationMatch);

            // Set the active persona based on the simulation's personaId
            const personaMatch = personas.find((p) => p.id === simulationMatch.personaId);
            if (personaMatch) setActivePersona(personaMatch);
          }
        }

        // If a sessionId query param is provided, set the active session ID
        if (sessionId) {
          setActiveSessionId(parseInt(sessionId));
        }
      }
    }

    // ==========================================
    // Manager active user
    // ==========================================
    /**
     * If a `userId` segment is present in the URL, update the manager's
     * active user in the global store. This triggers data fetching for that
     * user and allows `ManagedUserDashboard` to render the correct profile.
     *
     * Gated behind `ENABLE_MANAGER_API_ENDPOINTS` so it only runs when the
     * richer manager API is available (v2.0+).
     */
    if (userId) {
      if (FEATURE_FLAGS.ENABLE_MANAGER_API_ENDPOINTS) {
        // Set manager active user with just the ID to trigger data fetching in v2.0
        setManagerActiveUserId(userId);
      }
    }
  }, [
    certificationId,
    certifications,
    personas,
    sessionId,
    setActiveCertification,
    setActivePersona,
    setActiveSessionId,
    setActiveSimulation,
    setManagerActiveUserId,
    setManagerActiveUserIsLoading,
    simulationId,
    simulations,
    userId,
  ]);

  // ================================
  // Return
  // ================================
  /**
   * Expose the extracted param values so consumers can read them directly
   * without having to call `useParams` / parse query strings themselves.
   */
  return {
    certificationId,
    simulationId,
    sessionId,
  };
};
